/* eslint-disable no-nested-ternary */
/* eslint-disable no-new */
/* eslint-disable prefer-destructuring */
/* eslint-disable consistent-return */

import { useMutation, useApolloClient } from "@apollo/client";
import React, { useCallback, useState, useEffect } from 'react';
import {
  Popover, IconButton, Badge, Tooltip, List, ListItem,
  ListItemText, Typography, ListItemSecondaryAction,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { GET_USER_CHAT_GROUP_NOTIFICATIONS } from "@app/apollo/queries/chat-group.query";
import { MARK_GROUP_CHAT_AS_READ } from "@app/apollo/mutations";
import { redirectToRoute } from '@app/helpers';
import { useChat } from "@app/contexts/chatContext";

const color = "#1976d2";

const NotificationsMenu = () => {
  const { chatState, markGroupChatAsRead } = useChat();
  const apolloClient = useApolloClient();

  const [anchorEl, setAnchorEl] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [markGroupChatAsReadMutation] = useMutation(MARK_GROUP_CHAT_AS_READ);

  // ── Seed from Apollo cache on mount ──────────────────────────────────────
  useEffect(() => {
    try {
      const cached = apolloClient.cache.readQuery({
        query: GET_USER_CHAT_GROUP_NOTIFICATIONS,
      });
      if (cached?.userChatGroupNotifications?.length) {
        setNotifications(cached.userChatGroupNotifications);
      }
    } catch {
      // Cache empty — socket will populate
    }
  }, [apolloClient]);

  // ── React to incoming socket events via chatState ─────────────────────────
  // handleNewGroupMessage / handleNotificationNew already update
  // groupChatUnreadCounts on every GROUP_MESSAGE_NEW socket event,
  // so watching it here is all we need for real-time updates.
  useEffect(() => {
    const counts = chatState.groupChatUnreadCounts || {};

    setNotifications((prev) => {
      let changed = false;
      let next = [...prev];

      Object.entries(counts).forEach(([groupChatId, count]) => {
        if (count <= 0) return;

        const exists = next.find((n) => String(n.group?.id) === String(groupChatId));

        if (!exists) {
          // Placeholder until manual refresh hydrates full metadata
          next = [
            {
              __typename: "ChatGroupNotification",
              id: `local_${groupChatId}`,
              isRead: false,
              message: "New message",
              group: {
                __typename: "ChatGroup",
                id: groupChatId,
                description: "",
                opportunity: null,
              },
            },
            ...next,
          ];
          changed = true;
        } else if (exists.isRead) {
          // Group had been read before but just got a new message
          next = next.map((n) =>
            String(n.group?.id) === String(groupChatId) ? { ...n, isRead: false } : n
          );
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [chatState.groupChatUnreadCounts]);

  // ── Derived unread badge count ────────────────────────────────────────────
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  // ── Manual refresh — hits network, hydrates full metadata ─────────────────
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const { data } = await apolloClient.query({
        query: GET_USER_CHAT_GROUP_NOTIFICATIONS,
        fetchPolicy: "network-only",
      });
      if (data?.userChatGroupNotifications) {
        setNotifications(data.userChatGroupNotifications);
      }
    } catch (err) {
      console.error("Failed to refresh notifications", err);
    } finally {
      setIsRefreshing(false);
    }
  }, [apolloClient]);

  // ── Optimistic local read, then persist to server ─────────────────────────
  const markLocalRead = useCallback(async (groupId) => {
    // 1. Flip the notification to read locally
    setNotifications((prev) =>
      prev.map((n) =>
        String(n.group?.id) === String(groupId) ? { ...n, isRead: true } : n
      )
    );
    // 2. Zero the unread counter in chatState via the proper context API
    markGroupChatAsRead(groupId);
    // 3. Persist to server
    try {
      await markGroupChatAsReadMutation({ variables: { groupChatId: groupId } });
    } catch (err) {
      console.error("Failed to mark as read on server", err);
    }
  }, [markGroupChatAsRead, markGroupChatAsReadMutation]);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = async (notif) => {
    handleClose();
    const opportunitySlug = notif.group?.opportunity?.slug;
    if (opportunitySlug) {
      redirectToRoute({}, 'view_opportunity', {
        params: { slug: opportunitySlug },
        query: { openChat: 'true' },
      });
    }
    if (!notif.isRead) {
      await markLocalRead(notif.group?.id);
    }
  };

  const handleMarkAsRead = async (e, notif) => {
    e.stopPropagation();
    await markLocalRead(notif.group?.id);
  };

  const filtered = notifications.filter((n) => {
    if (tabValue === 1) return !n.isRead;
    if (tabValue === 2) return n.isRead;
    return true;
  });

  return (
    <>
      <IconButton aria-label="notifications" onClick={handleOpen} color="inherit">
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { width: 400, maxHeight: 420, p: 0, borderRadius: 2, overflow: "hidden" },
        }}
      >
        {/* HEADER */}
        <div style={{
          display: "flex", alignItems: "center",
          borderBottom: "1px solid #eee", padding: 8, paddingRight: 12,
        }}>
          <div style={{ flex: 1, display: "flex" }}>
            {["All", "Unread", "Read"].map((label, index) => {
              const isActive = tabValue === index;
              const borderRadius =
                index === 0 ? "6px 0 0 0" : index === 2 ? "0 6px 0 0" : "0";
              return (
                <button
                  type="button"
                  key={label}
                  onClick={() => setTabValue(index)}
                  style={{
                    flex: 1, padding: "6px 0",
                    background: isActive ? color : "transparent",
                    color: isActive ? "#fff" : color,
                    border: `1px solid ${color}`,
                    fontWeight: isActive ? "bold" : "normal",
                    cursor: "pointer", fontSize: 15,
                    borderRadius, position: "relative", transition: "0.2s",
                  }}
                >
                  {label}
                  {isActive && (
                    <span style={{
                      position: "absolute", left: "50%",
                      transform: "translateX(-50%)", bottom: -2,
                      width: 0, height: 0,
                      borderLeft: "8px solid transparent",
                      borderRight: "8px solid transparent",
                      borderTop: `8px solid ${color}`,
                    }} />
                  )}
                </button>
              );
            })}
          </div>

          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                sx={{ ml: 1 }}
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                <RefreshIcon
                  fontSize="small"
                  sx={{
                    transition: "transform 0.3s",
                    animation: isRefreshing ? "spin 1s linear infinite" : "none",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        {/* LIST */}
        <div style={{ padding: 12, maxHeight: 330, overflowY: "auto" }}>
          {filtered.length > 0 ? (
            <List dense>
              {filtered.map((notif) => (
                <ListItem
                  key={notif.id}
                  button
                  onClick={() => handleNotificationClick(notif)}
                  sx={{
                    backgroundColor: notif.isRead
                      ? "rgba(0,0,0,0.03)"
                      : "rgba(25,118,210,0.08)",
                    mb: 0.5, borderRadius: 1, cursor: "pointer", pr: 6,
                  }}
                >
                  <ListItemText
                    primary={notif.message}
                    secondary={notif.group?.description ?? ""}
                    primaryTypographyProps={{
                      fontWeight: notif.isRead ? "normal" : "bold",
                      fontSize: "0.875rem",
                    }}
                  />
                  <ListItemSecondaryAction>
                    {notif.isRead ? (
                      <Tooltip title="Read">
                        <DoneAllIcon sx={{ fontSize: 20, color: "success.main", opacity: 0.7 }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Mark as read">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => handleMarkAsRead(e, notif)}
                          sx={{ "&:hover": { backgroundColor: "rgba(25,118,210,0.15)" } }}
                        >
                          <DoneIcon sx={{ fontSize: 20, color }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography align="center" sx={{ mt: 2, opacity: 0.7 }}>
              No notifications
            </Typography>
          )}
        </div>
      </Popover>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default NotificationsMenu;

// import { useMutation, useQuery } from "@apollo/client";
// import React, { useCallback, useState, useEffect } from 'react';
// import {
//   Popover,
//   IconButton,
//   Badge,
//   Tooltip,
//   List,
//   ListItem,
//   ListItemText,
//   Typography,
//   ListItemSecondaryAction,
// } from "@mui/material";
// import RefreshIcon from "@mui/icons-material/Refresh";
// import NotificationsIcon from "@mui/icons-material/Notifications";
// import DoneIcon from "@mui/icons-material/Done";
// import DoneAllIcon from "@mui/icons-material/DoneAll";
// import { GET_USER_CHAT_GROUP_NOTIFICATIONS } from "@app/apollo/queries/chat-group.query";
// import { MARK_GROUP_CHAT_AS_READ } from "@app/apollo/mutations";
// import { redirectToRoute } from '@app/helpers';
// import { useChat } from "@app/contexts/chatContext";

// const color = "#1976d2";

// const NotificationsMenu = () => {
//   const { chatState } = useChat();
//   const [anchorEl, setAnchorEl] = useState(null);
//   const [tabValue, setTabValue] = useState(0);

//   // -------- LOAD NOTIFICATIONS LIST ----------
//   const {
//     loading: listLoading,
//     error: listError,
//     data: listData,
//     refetch: refetchList,
//   } = useQuery(GET_USER_CHAT_GROUP_NOTIFICATIONS, {
//     fetchPolicy: "cache-and-network",
//   });

//   const [markGroupChatAsRead] = useMutation(MARK_GROUP_CHAT_AS_READ);

//   // Notifications array
//   const notifications = listData?.userChatGroupNotifications ?? [];

//   const unreadCount = Object.values(chatState.groupChatUnreadCounts || {}).reduce(
//     (sum, count) => sum + count,
//     0
//   );

//   const handleOpen = (e) => setAnchorEl(e.currentTarget);
//   const handleClose = () => setAnchorEl(null);

//   // Refresh both queries
//   const handleRefresh = useCallback(async () => {
//     await refetchList();
//   }, [refetchList]);

//   useEffect(() => {
//     if (unreadCount > 0) {
//       handleRefresh();
//     }
//   }, [unreadCount, handleRefresh]);

//   // Filtered notifications
//   const filtered = notifications.filter((n) => {
//     if (tabValue === 1) return !n.isRead; // unread
//     if (tabValue === 2) return n.isRead;  // read
//     return true;
//   });

//   const handleNotificationClick = async (notif) => {
//     try {
//       // Close the popover
//       handleClose();

//       console.log("Notification clicked:", notif);

//       // Navigate to opportunity dashboard with chat open
//       const opportunitySlug = notif.group?.opportunity?.slug;

//       console.log("Navigating to opportunity:", opportunitySlug);

//       if (opportunitySlug) {
//         redirectToRoute({}, 'view_opportunity', {
//           params: { slug: opportunitySlug },
//           query: { openChat: 'true' }
//         });
//       }

//       // Mark as read
//       if (!notif.isRead) {
//         await markGroupChatAsRead({
//           variables: { groupChatId: notif.group.id },
//         });
//         await handleRefresh();
//       }
//     } catch (error) {
//       console.error("Failed to handle notification click", error);
//     }
//   };

//   const handleMarkAsRead = async (e, notif) => {
//     e.stopPropagation();

//     try {
//       await markGroupChatAsRead({
//         variables: { groupChatId: notif.group.id },
//       });

//       await handleRefresh();
//     } catch (error) {
//       console.error("Mark as read failed", error);
//     }
//   };

//   return (
//     <>
//       <IconButton aria-label="notifications" onClick={handleOpen} color="inherit">
//         <Badge badgeContent={unreadCount} color="error">
//           <NotificationsIcon />
//         </Badge>
//       </IconButton>

//       <Popover
//         open={Boolean(anchorEl)}
//         anchorEl={anchorEl}
//         onClose={handleClose}
//         anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//         transformOrigin={{ vertical: "top", horizontal: "right" }}
//         PaperProps={{
//           sx: {
//             width: 400,
//             maxHeight: 420,
//             p: 0,
//             borderRadius: 2,
//             overflow: "hidden",
//           },
//         }}
//       >
//         {/* HEADER */}
//         <div
//           style={{
//             display: "flex",
//             alignItems: "center",
//             borderBottom: "1px solid #eee",
//             padding: 8,
//             paddingRight: 12,
//           }}
//         >
//           <div style={{ flex: 1, display: "flex" }}>
//             {["All", "Unread", "Read"].map((label, index) => {
//               const isActive = tabValue === index;
//               const getBorderRadius = (indx) => {
//                 if (indx === 0) return "6px 0 0 0";
//                 if (indx === 2) return "0 6px 0 0";
//                 return "0";
//               };

//               return (
//                 <button
//                   type="button"
//                   key={label}
//                   onClick={() => setTabValue(index)}
//                   style={{
//                     flex: 1,
//                     padding: "6px 0",
//                     background: isActive ? color : "transparent",
//                     color: isActive ? "#fff" : color,
//                     border: `1px solid ${color}`,
//                     fontWeight: isActive ? "bold" : "normal",
//                     cursor: "pointer",
//                     fontSize: 15,
//                     borderRadius: getBorderRadius(index),
//                     position: "relative",
//                     transition: "0.2s",
//                   }}
//                 >
//                   {label}
//                   {isActive && (
//                     <span
//                       style={{
//                         position: "absolute",
//                         left: "50%",
//                         transform: "translateX(-50%)",
//                         bottom: -2,
//                         width: 0,
//                         height: 0,
//                         borderLeft: "8px solid transparent",
//                         borderRight: "8px solid transparent",
//                         borderTop: `8px solid ${color}`,
//                       }}
//                     />
//                   )}
//                 </button>
//               );
//             })}
//           </div>

//           <Tooltip title="Refresh">
//             <IconButton size="small" sx={{ ml: 1 }} onClick={handleRefresh}>
//               <RefreshIcon fontSize="small" />
//             </IconButton>
//           </Tooltip>
//         </div>

//         {/* LIST */}
//         <div style={{ padding: 12, maxHeight: 330, overflowY: "auto" }}>
//           {listLoading ? (
//             <Typography align="center" sx={{ mt: 2 }}>
//               Loading...
//             </Typography>
//           ) : listError ? (
//             <Typography align="center" sx={{ mt: 2, color: "red" }}>
//               Failed to load
//             </Typography>
//           ) : filtered.length > 0 ? (
//             <List dense>
//               {filtered.map((notif) => (
//                 <ListItem
//                   key={notif.id}
//                   button
//                   onClick={() => handleNotificationClick(notif)}
//                   sx={{
//                     backgroundColor: notif.isRead
//                       ? "rgba(0,0,0,0.03)"
//                       : "rgba(25,118,210,0.08)",
//                     mb: 0.5,
//                     borderRadius: 1,
//                     cursor: 'pointer',
//                     pr: 6,
//                   }}
//                 >
//                   <ListItemText
//                     primary={notif.message}
//                     secondary={notif.group?.description ?? ""}
//                     primaryTypographyProps={{
//                       fontWeight: notif.isRead ? "normal" : "bold",
//                       fontSize: "0.875rem",
//                     }}
//                   />
//                   <ListItemSecondaryAction>
//                     {notif.isRead ? (
//                       <Tooltip title="Read">
//                         <DoneAllIcon
//                           sx={{
//                             fontSize: 20,
//                             color: 'success.main',
//                             opacity: 0.7
//                           }}
//                         />
//                       </Tooltip>
//                     ) : (
//                       <Tooltip title="Mark as read">
//                         <IconButton
//                           edge="end"
//                           size="small"
//                           onClick={(e) => handleMarkAsRead(e, notif)}
//                           sx={{
//                             '&:hover': {
//                               backgroundColor: 'rgba(25,118,210,0.15)',
//                             }
//                           }}
//                         >
//                           <DoneIcon
//                             sx={{
//                               fontSize: 20,
//                               color
//                             }}
//                           />
//                         </IconButton>
//                       </Tooltip>
//                     )}
//                   </ListItemSecondaryAction>
//                 </ListItem>
//               ))}
//             </List>
//           ) : (
//             <Typography align="center" sx={{ mt: 2, opacity: 0.7 }}>
//               No notifications
//             </Typography>
//           )}
//         </div>
//       </Popover>
//     </>
//   );
// };

// export default NotificationsMenu;
